import React, {forwardRef, Fragment, useImperativeHandle, useState} from 'react';
import {FormProvider, useForm, useFormContext} from 'react-hook-form';
import {FormSession} from '../FormSession';
import {ReadOnlySession} from '../ReadOnlySession';
import {TherapySessionsData} from '../TherapySessions/types';
import {EditSessionProps, EditSessionRef} from './types';

const EditSession = forwardRef<EditSessionRef, EditSessionProps>(
	({index, sessionData, onValidation}, ref) => {
		const [isEditMode, setIsEditMode] = useState<boolean>(false);
		const [editKey, setEditKey] = useState<number>(0);
		const mainFormContext = useFormContext<TherapySessionsData>();
		const watchedTherapyType = mainFormContext.watch('therapyType');
		const localForm = useForm<TherapySessionsData>({
			mode: 'onSubmit',
		});

		useImperativeHandle(ref, () => ({
			editSession: () => {
				setEditKey(prev => prev + 1);
				setIsEditMode(true);

				const timer = setTimeout(() => {
					const serviceDateName = 'sessions.0.serviceDate';
					const sessionValueName = 'sessions.0.sessionValue';
					const isSavedName = 'sessions.0.isSaved';
					const isIdName = 'sessions.0.id';

					const serviceDateInput = document.querySelector(
						`input[name*="${serviceDateName}"]`,
					) as HTMLInputElement;
					const sessionValueInput = document.querySelector(
						`input[name*="${sessionValueName}"]`,
					) as HTMLInputElement;

					if (serviceDateInput) {
						const dateValue = sessionData?.serviceDate || '';
						localForm.setValue(serviceDateName as 'sessions.0.serviceDate', dateValue);
						serviceDateInput.value = dateValue;
						serviceDateInput.dispatchEvent(new Event('input', {bubbles: true}));
					}

					if (!sessionValueInput) {
						return;
					}

					const sessionValue = sessionData?.sessionValue || '';
					localForm.setValue(sessionValueName as 'sessions.0.sessionValue', sessionValue);
					sessionValueInput.value = sessionValue;
					sessionValueInput.dispatchEvent(new Event('input', {bubbles: true}));

					if (!sessionValue) {
						return;
					}

					const isSaved = sessionData?.isSaved || false;
					localForm.setValue(isSavedName as 'sessions.0.isSaved', isSaved);

					const isId = sessionData?.id || '';
					localForm.setValue(isIdName as 'sessions.0.id', isId);
				}, 0);

				return () => clearTimeout(timer);
			},
			cancelEdit: () => {
				localForm.clearErrors();
				localForm.reset();
				setIsEditMode(false);
			},
			saveChanges: async options => {
				const isValid = await localForm.trigger();
				if (!isValid || options?.isJustValidation) {
					return isValid;
				}

				if (!options?.success) {
					return true;
				}

				const editedData = localForm.getValues('sessions.0');

				const {success} = onValidation({
					currentSession: editedData,
					currentTherapyType: watchedTherapyType?.code,
					currentIndex: 0,
					form: localForm,
				});

				if (!success) {
					return false;
				}

				mainFormContext.setValue(
					`sessions.${index}`,
					{...editedData, id: sessionData?.id || ''},
					{
						shouldValidate: true,
						shouldDirty: true,
						shouldTouch: true,
					},
				);

				setIsEditMode(!options?.success);
				return true;
			},
			hasUnsavedChanges: () => {
				if (!isEditMode) {
					return false;
				}

				const currentData = localForm.getValues('sessions.0');
				const currentServiceDate = currentData?.serviceDate?.trim() || '';
				const currentSessionValue = currentData?.sessionValue?.trim() || '';
				const originalServiceDate = sessionData?.serviceDate?.trim() || '';
				const originalSessionValue = sessionData?.sessionValue?.trim() || '';

				return (
					currentServiceDate !== originalServiceDate ||
					currentSessionValue !== originalSessionValue
				);
			},
			getCurrentData: () => {
				if (!isEditMode) {
					return sessionData || null;
				}
				const currentData = localForm.getValues('sessions.0');
				return {
					...currentData,
					id: sessionData?.id || '',
				};
			},
		}));

		return (
			<Fragment>
				{isEditMode ? (
					<FormProvider {...localForm}>
						<FormSession
							key={`edit-${index}-${editKey}`}
							index={0}
							control={localForm.control}
						/>
					</FormProvider>
				) : (
					<ReadOnlySession sessionData={sessionData} />
				)}
			</Fragment>
		);
	},
);

export {EditSession, type EditSessionRef};
